import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { Toaster } from "@/components/ui/sonner";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import NewsList from "@/pages/public/NewsList";
import Article from "@/pages/public/Article";
import Games from "@/pages/public/Games";
import GameDetail from "@/pages/public/GameDetail";
import Esports from "@/pages/public/Esports";
import SearchPage from "@/pages/public/SearchPage";
import StaticPage from "@/pages/public/StaticPage";
import ReportResult from "@/pages/public/ReportResult";
import PrintQR from "@/pages/public/PrintQR";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminPages from "@/pages/admin/AdminPages";
import Tournaments from "@/pages/public/Tournaments";
import TournamentDetail from "@/pages/public/TournamentDetail";
import Profile from "@/pages/public/Profile";
import CreateTournament from "@/pages/public/CreateTournament";
import ManageTournament from "@/pages/public/ManageTournament";
import RegisterTeam from "@/pages/public/RegisterTeam";
import PaymentReturn from "@/pages/public/PaymentReturn";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminNews from "@/pages/admin/AdminNews";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTournaments from "@/pages/admin/AdminTournaments";
import AdminGames from "@/pages/admin/AdminGames";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminSEO from "@/pages/admin/AdminSEO";
import AdminAccounts from "@/pages/admin/AdminAccounts";
import AdminComments from "@/pages/admin/AdminComments";
import AdminReports from "@/pages/admin/AdminReports";

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] text-white/60">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/news" element={<NewsList />} />
        <Route path="/news/:slug" element={<Article />} />
        <Route path="/games" element={<Games />} />
        <Route path="/games/:id" element={<GameDetail />} />
        <Route path="/community" element={<Esports />} />
        <Route path="/esports" element={<Navigate to="/community" replace />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/report/:token" element={<ReportResult />} />
        <Route path="/print/qr/:id" element={<PrintQR />} />
        <Route path="/contact" element={<StaticPage slug="contact" />} />
        <Route path="/about" element={<StaticPage slug="about" />} />
        <Route path="/privacy" element={<StaticPage slug="privacy" />} />
        <Route path="/terms" element={<StaticPage slug="terms" />} />
        <Route path="/help" element={<StaticPage slug="help" />} />
        <Route path="/how-to-tournament" element={<Navigate to={{ pathname: "/help", hash: "#tournaments" }} replace />} />
        <Route path="/how-to-stream" element={<Navigate to={{ pathname: "/help", hash: "#live-streaming" }} replace />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/tournaments/create" element={<CreateTournament />} />
        <Route path="/tournaments/:slug" element={<TournamentDetail />} />
        <Route path="/tournaments/:slug/register" element={<RegisterTeam />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/tournaments/:id" element={<ManageTournament />} />
        <Route path="/payment/return" element={<PaymentReturn />} />
      </Route>

      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminLayout />
          </RequireAdmin>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="news" element={<AdminNews />} />
        <Route path="content" element={<AdminGames />} />
        <Route path="tournaments" element={<AdminTournaments />} />
        <Route path="comments" element={<AdminComments />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="homepage" element={<AdminHomepage />} />
        <Route path="pages" element={<AdminPages />} />
        <Route path="newsletter" element={<AdminNewsletter />} />
        <Route path="seo" element={<AdminSEO />} />
        <Route path="accounts" element={<AdminAccounts />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ConfigProvider>
          <AuthProvider>
            <SettingsProvider>
              <AppRouter />
              <Toaster position="top-right" />
            </SettingsProvider>
          </AuthProvider>
        </ConfigProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
